/**
 * Vercel Flags helpers — GA4 experiment tracking, flag registry, and utilities.
 *
 * - Only flags with `tracked: true` in FLAG_DEFINITIONS are reported to GA4/FullStory/Snowplow.
 * - Boolean feature-gate flags are NOT tracked (they're not experiments).
 * - Value -1 means "off" and is excluded from GA4 reporting.
 */

/**
 * Check if a resolved flag value equals a specific variant.
 * @param {number|boolean} value - Resolved flag value
 * @param {number|boolean} variant - Expected variant
 * @returns {boolean}
 */
export const isVariant = (value, variant) => value === variant;

/**
 * Build GA4-compatible experiment payload from resolved Vercel A/B flags.
 *
 * Produces the same shape as LD's `User_Information_GAExperimentId` pipe string:
 *   "flag-key-a.1|flag-key-b.0"
 *
 * Flags with value -1 (off / not in experiment) are excluded.
 *
 * @param {Record<string, number>} resolvedFlags - Map of flag key → resolved numeric value
 * @param {string} userId - The bx_user_id (anonymous UUID)
 * @returns {{ User_Information_GAExperimentId: string|null, User_Information_GAExperimentUserId: string }}
 */
export const buildVercelExperimentsGA4 = (resolvedFlags, userId) => {
  const entries = Object.entries(resolvedFlags)
    .filter(([, value]) => value !== -1)
    .map(([key, value]) => `${key}.${value}`);

  return {
    User_Information_GAExperimentId:
      entries.length > 0 ? entries.join('|') : null,
    User_Information_GAExperimentUserId: userId,
  };
};

/**
 * Merge LD and Vercel GA4 experiment payloads into a single pipe-joined string.
 *
 * @param {object} ldGa4 - LD's ga4 object: { User_Information_GAExperimentId, User_Information_GAExperimentUserId }
 * @param {object} vercelGa4 - Vercel's ga4 object (same shape)
 * @returns {object} Merged ga4 object with combined pipe string
 */
/**
 * Extract resolved `x-vf-*` flag values from a Pages Router `req.headers` map.
 *
 * Returns `{ 'flag-key': numericValue }` for every `x-vf-*` request header
 * with a numeric value, skipping `x-vf-experiments-ga4` (which holds the GA4
 * pipe string, not a flag value).
 *
 *
 * @param {Record<string, string | string[] | undefined>} headers
 * @returns {Record<string, number>}
 */
export const normalizeFlagKeys = (headers = {}) => {
  const validKeys = {};
  for (const [rawKey, rawVal] of Object.entries(headers)) {
    const key = rawKey.toLowerCase();
    if (key === 'x-vf-experiments-ga4') continue;
    if (!key.startsWith('x-vf-')) continue;
    const expKey = key.slice(5);
    const val = Number(rawVal);
    if (!Number.isNaN(val)) validKeys[expKey] = val;
  }
  return validKeys;
};

export const mergeExperimentsGA4 = (ldGa4 = {}, vercelGa4 = {}) => {
  const parts = [
    ldGa4.User_Information_GAExperimentId,
    vercelGa4.User_Information_GAExperimentId,
  ].filter(Boolean);

  return {
    User_Information_GAExperimentId: parts.length > 0 ? parts.join('|') : null,
    User_Information_GAExperimentUserId:
      ldGa4.User_Information_GAExperimentUserId ||
      vercelGa4.User_Information_GAExperimentUserId ||
      null,
  };
};
